import type { ValueContext } from './value-context';

/**
 * Token types for the expression lexer.
 */
type TokenType = 'number' | 'path' | 'op' | 'lparen' | 'rparen';

interface Token {
  type: TokenType;
  value: string;
}

/**
 * Tokenize an expression string into a list of tokens.
 *
 * Hyphen ambiguity rule: greedy path matching.
 * `f-tangent.slope` (no spaces around `-`) → single path token.
 * `f.h - g.h` (spaces around `-`) → three tokens: path, op, path.
 *
 * Path pattern: [a-zA-Z_][a-zA-Z0-9_-]*(.[a-zA-Z_][a-zA-Z0-9_]*)*
 */
function tokenize(expr: string): Token[] {
  const tokens: Token[] = [];
  let i = 0;

  while (i < expr.length) {
    const ch = expr[i];

    // Skip whitespace
    if (ch === ' ' || ch === '\t') {
      i++;
      continue;
    }

    // Number literal (including decimals)
    if (ch >= '0' && ch <= '9') {
      let num = '';
      while (i < expr.length && ((expr[i] >= '0' && expr[i] <= '9') || expr[i] === '.')) {
        num += expr[i];
        i++;
      }
      tokens.push({ type: 'number', value: num });
      continue;
    }

    // Identifier/path: starts with [a-zA-Z_]
    if ((ch >= 'a' && ch <= 'z') || (ch >= 'A' && ch <= 'Z') || ch === '_') {
      let path = '';
      // Match: [a-zA-Z_][a-zA-Z0-9_-]*(.[a-zA-Z_][a-zA-Z0-9_]*)*
      // First segment (allows hyphens)
      while (
        i < expr.length &&
        ((expr[i] >= 'a' && expr[i] <= 'z') ||
          (expr[i] >= 'A' && expr[i] <= 'Z') ||
          (expr[i] >= '0' && expr[i] <= '9') ||
          expr[i] === '_' ||
          expr[i] === '-')
      ) {
        path += expr[i];
        i++;
      }
      // Dot-separated segments
      while (i < expr.length && expr[i] === '.') {
        // Peek ahead to ensure next char is a valid segment start (letter, digit, or underscore)
        if (
          i + 1 < expr.length &&
          ((expr[i + 1] >= 'a' && expr[i + 1] <= 'z') ||
            (expr[i + 1] >= 'A' && expr[i + 1] <= 'Z') ||
            (expr[i + 1] >= '0' && expr[i + 1] <= '9') ||
            expr[i + 1] === '_')
        ) {
          path += '.';
          i++; // skip the dot
          while (
            i < expr.length &&
            ((expr[i] >= 'a' && expr[i] <= 'z') ||
              (expr[i] >= 'A' && expr[i] <= 'Z') ||
              (expr[i] >= '0' && expr[i] <= '9') ||
              expr[i] === '_')
          ) {
            path += expr[i];
            i++;
          }
        } else {
          break;
        }
      }
      tokens.push({ type: 'path', value: path });
      continue;
    }

    // Multi-char operators (check before single-char to avoid ambiguity)
    if (i + 1 < expr.length) {
      const two = ch + expr[i + 1];
      if (two === '>=' || two === '<=' || two === '==' || two === '!=' || two === '&&' || two === '||') {
        tokens.push({ type: 'op', value: two });
        i += 2;
        continue;
      }
    }

    // Single-char operators
    if (ch === '+' || ch === '-' || ch === '*' || ch === '/' || ch === '>' || ch === '<') {
      tokens.push({ type: 'op', value: ch });
      i++;
      continue;
    }

    // Negation operator
    if (ch === '!') {
      tokens.push({ type: 'op', value: '!' });
      i++;
      continue;
    }

    // Parentheses
    if (ch === '(') {
      tokens.push({ type: 'lparen', value: '(' });
      i++;
      continue;
    }
    if (ch === ')') {
      tokens.push({ type: 'rparen', value: ')' });
      i++;
      continue;
    }

    throw new Error(`Unexpected character '${ch}' in expression: ${expr}`);
  }

  return tokens;
}

/**
 * Recursive-descent parser for expressions on ValueContext paths.
 *
 * Supports: numeric literals, ValueContext paths, arithmetic (+, -, *, /),
 * comparison (>, <, >=, <=, ==, !=), logical (&&, ||, !), parentheses, standard precedence.
 * Unary minus is supported.
 *
 * Precedence (lowest → highest):
 * 1. || (logical OR)
 * 2. && (logical AND)
 * 3. ==, !=, >, <, >=, <= (comparison)
 * 4. +, - (addition/subtraction)
 * 5. *, / (multiplication/division)
 * 6. unary -, +, ! and primaries
 *
 * Comparisons and logical ops return 1 (true) / 0 (false).
 *
 * Returns a thunk `() => number` that evaluates the expression reactively.
 */
export function parseExpression(expr: string, valueContext: ValueContext): () => number {
  const tokens = tokenize(expr);
  let pos = 0;

  function peek(): Token | undefined {
    return tokens[pos];
  }

  function consume(): Token {
    return tokens[pos++];
  }

  // primary: number | path | '(' expr ')' | unary minus
  function parsePrimary(): () => number {
    const token = peek();

    if (!token) {
      throw new Error(`Unexpected end of expression: ${expr}`);
    }

    // Unary minus
    if (token.type === 'op' && token.value === '-') {
      consume();
      const operand = parsePrimary();
      return () => -operand();
    }

    // Unary plus
    if (token.type === 'op' && token.value === '+') {
      consume();
      return parsePrimary();
    }

    // Logical negation
    if (token.type === 'op' && token.value === '!') {
      consume();
      const operand = parsePrimary();
      return () => (operand() !== 0 ? 0 : 1);
    }

    if (token.type === 'number') {
      consume();
      const val = parseFloat(token.value);
      return () => val;
    }

    if (token.type === 'path') {
      consume();
      const path = token.value;
      return () => valueContext.getValue(path);
    }

    if (token.type === 'lparen') {
      consume();
      const inner = parseLogicalOr();
      const close = peek();
      if (!close || close.type !== 'rparen') {
        throw new Error(`Expected ')' in expression: ${expr}`);
      }
      consume();
      return inner;
    }

    throw new Error(`Unexpected token '${token.value}' in expression: ${expr}`);
  }

  // mulDiv: primary (('*' | '/') primary)*
  function parseMulDiv(): () => number {
    let left = parsePrimary();

    while (peek()?.type === 'op' && (peek()!.value === '*' || peek()!.value === '/')) {
      const op = consume().value;
      const right = parsePrimary();
      const l = left;
      if (op === '*') {
        left = () => l() * right();
      } else {
        left = () => l() / right();
      }
    }

    return left;
  }

  // addSub: mulDiv (('+' | '-') mulDiv)*
  function parseAddSub(): () => number {
    let left = parseMulDiv();

    while (peek()?.type === 'op' && (peek()!.value === '+' || peek()!.value === '-')) {
      const op = consume().value;
      const right = parseMulDiv();
      const l = left;
      if (op === '+') {
        left = () => l() + right();
      } else {
        left = () => l() - right();
      }
    }

    return left;
  }

  // comparison: addSub (('>' | '<' | '>=' | '<=' | '==' | '!=') addSub)?
  // Non-chaining: only one comparison per level
  function parseComparison(): () => number {
    let left = parseAddSub();
    const t = peek();
    if (
      t?.type === 'op' &&
      (t.value === '>' ||
        t.value === '<' ||
        t.value === '>=' ||
        t.value === '<=' ||
        t.value === '==' ||
        t.value === '!=')
    ) {
      const op = consume().value;
      const right = parseAddSub();
      const l = left;
      switch (op) {
        case '>':
          left = () => (l() > right() ? 1 : 0);
          break;
        case '<':
          left = () => (l() < right() ? 1 : 0);
          break;
        case '>=':
          left = () => (l() >= right() ? 1 : 0);
          break;
        case '<=':
          left = () => (l() <= right() ? 1 : 0);
          break;
        case '==':
          left = () => (l() === right() ? 1 : 0);
          break;
        case '!=':
          left = () => (l() !== right() ? 1 : 0);
          break;
      }
    }
    return left;
  }

  // logicalAnd: comparison ('&&' comparison)*
  function parseLogicalAnd(): () => number {
    let left = parseComparison();
    while (peek()?.type === 'op' && peek()!.value === '&&') {
      consume();
      const right = parseComparison();
      const l = left;
      left = () => (l() !== 0 && right() !== 0 ? 1 : 0);
    }
    return left;
  }

  // logicalOr: logicalAnd ('||' logicalAnd)*
  function parseLogicalOr(): () => number {
    let left = parseLogicalAnd();
    while (peek()?.type === 'op' && peek()!.value === '||') {
      consume();
      const right = parseLogicalAnd();
      const l = left;
      left = () => (l() !== 0 || right() !== 0 ? 1 : 0);
    }
    return left;
  }

  const result = parseLogicalOr();

  if (pos < tokens.length) {
    throw new Error(`Unexpected token '${tokens[pos].value}' after expression: ${expr}`);
  }

  return result;
}

/**
 * Extract all ValueContext path references from an expression string.
 * Used for dependency resolution — returns the element IDs referenced.
 */
export function extractExpressionPaths(expr: string): string[] {
  try {
    const tokens = tokenize(expr);
    return tokens.filter((t) => t.type === 'path').map((t) => t.value);
  } catch {
    return [];
  }
}
